import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

/* --------------------------------
   Styled Component Customizations
---------------------------------- */

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": { borderBottom: 0 },
  "&::before": { display: "none" },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "rgba(0,0,0,.03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    { transform: "rotate(90deg)" },
  [`& .${accordionSummaryClasses.content}`]: { marginLeft: theme.spacing(1) },
  ...theme.applyStyles?.("dark", {
    backgroundColor: "rgba(255,255,255,.05)",
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0,0,0,.125)",
}));

/* --------------------------------
   Main Sidebar Component
---------------------------------- */

const LeftSidebarMenu = ({ toggleActive }) => {
  const [expanded, setExpanded] = useState("panel1");
  const location = useLocation();

  const handleChange = (panel) => (_e, newExpanded) =>
    setExpanded(newExpanded ? panel : false);

  const isActiveLink = (path) =>
    location.pathname === path ? "active" : "";

  // Optional Trezo dark‑theme logic for the beauty‑salon page
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    // Check if the global dark theme is applied to the HTML tag
    // This allows the sidebar to react to the main theme toggle
    const checkDarkTheme = () => {
      const htmlHasDark = document.documentElement.classList.contains("dark-theme");
      setIsDark(htmlHasDark);
    };

    // Initial check
    checkDarkTheme();

    // Observe changes to the class attribute on the <html> element
    const observer = new MutationObserver(checkDarkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Style object to force white text on sidebar links in dark mode
  const sidebarLinkSx = {
    // Light mode default (inherit from CSS)
    
    // Dark mode override
    ".dark-theme &": {
      color: "#fff !important",
      "&:hover": {
        color: "#fff !important", 
      },
      "&.active": {
        color: "#fff !important", 
      }
    }
  };

  return (
    <Box
      className={`leftSidebarDark hide-for-horizontal-nav ${isDark ? "dark-theme" : ""}`}
      // Apply the sx prop to the container to cascade styles down if needed, 
      // or we can apply it directly to the links via the `sx` prop on a Box wrapper.
      sx={{
        ".dark-theme & .sidemenu-link": {
          color: "#fff !important",
        },
        ".dark-theme & .sidemenu-link:hover": {
          color: "#fff !important",
        },
        ".dark-theme & .sidemenu-link.active": {
          color: "#fff !important",
        },
        ".dark-theme & .mat-summary .title": {
           color: "#fff !important",
        },
        ".dark-theme & .mat-summary i": {
           color: "#fff !important",
        },
        ".dark-theme & .mat-summary .trezo-badge": {
           color: "#000 !important", // Keep badge readable or adjust as needed
           backgroundColor: "#fff !important"
        }
      }}
    >
      <Box className="left-sidebar-menu">
        {/* ---------- LOGO ---------- */}
        <Box className="logo">
          <Link to="/dashboard/forms">
            <img
              src="/images/logo1.png"
              alt="logo-icon"
              width={26}
              height={26}
            />
            <Typography component="span">NeuroEZ</Typography>
          </Link>
        </Box>

        {/* ---------- BURGER MENU ---------- */}
        <Box className="burger-menu" onClick={toggleActive}>
          <Typography component="span" className="top-bar" />
          <Typography component="span" className="middle-bar" />
          <Typography component="span" className="bottom-bar" />
        </Box>

        {/* ---------- SIDEBAR CONTENT ---------- */}
        <Box className="sidebar-inner">
          <Box className="sidebar-menu">
            {/* <Typography
              className="sub-title"
              sx={{
                display: "block",
                fontWeight: 500,
                textTransform: "uppercase",
              }}
            >
              MAIN
            </Typography> */}

            {/* ---- DASHBOARD accordion ---- */}
            <Accordion
              expanded={expanded === "panel1"}
              onChange={handleChange("panel1")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel1d-content"
                id="panel1d-header"
              >
                <i className="material-symbols-outlined">dashboard</i>
                <Typography component="span" className="title">
                  Dashboard
                </Typography>
                <Typography component="span" className="trezo-badge">
                  2
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                   <li className="sidemenu-item">
                    <Link
                      to="/dashboard/forms"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/forms"
                      )}`}
                    >
                      Forms
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/patient-profile"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/patient-profile"
                      )}`}
                    >
                      Patient Profile
                    </Link>
                  </li>

                 
                </ul>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LeftSidebarMenu;